import type { ProfileCardSpan } from '@/components/common/ProfileCardGrid';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';
import type { InvestorProfileFormValues } from './schema';
import { INVESTOR_CARD_STRINGS as C } from './investor-form.constants';

/**
 * The investor profile's editable cards (FR-175). Preferred industries are
 * part of "What we invest in" (`preferences`), as in the approved mockup.
 */
export type InvestorSectionId =
  'about' | 'location' | 'preferences' | 'founder' | 'geographies' | 'colleagues';

export interface InvestorSectionConfig {
  title: string;
  /** Form fields the section's side panel edits; only these are validated (FR-167). */
  fields: readonly (keyof InvestorProfileFormValues)[];
  span: ProfileCardSpan;
}

export const INVESTOR_SECTIONS: Record<InvestorSectionId, InvestorSectionConfig> = {
  about: {
    title: C.ABOUT,
    fields: [
      'logoFileKey',
      'profilePhotoFileKey',
      'firmName',
      'investorType',
      'contactName',
      'contactTitle',
      'phone',
      'websiteUrl',
      'linkedinUrl',
      'intro',
    ],
    span: 12,
  },
  location: {
    title: C.LOCATION,
    fields: [
      'addressStreet',
      'addressStreet2',
      'addressCity',
      'addressState',
      'addressPostalCode',
      'addressCountry',
    ],
    span: 7,
  },
  preferences: {
    title: C.WHAT_WE_INVEST_IN,
    fields: [
      'prefAmountLow',
      'prefAmountHigh',
      'prefMinRevenue',
      'prefRecurringRevenue',
      'preferredStages',
      'prefB2b',
      'prefB2c',
      'prefB2g',
      'prefSaas',
      'prefAi',
      'prefLeadOrFollow',
      'activelyDeploying',
      'prefOtherCriteria',
      'industries',
    ],
    span: 7,
  },
  founder: {
    title: C.FOUNDER_PREFERENCES,
    fields: [
      'prefFemaleLed',
      'prefUnderrepresented',
      'prefUnderrepresentedCategories',
      'interestOfficeHours',
      'interestFounderFunderEvents',
    ],
    span: 5,
  },
  geographies: {
    title: C.GEOGRAPHIES,
    fields: ['geographies'],
    span: 5,
  },
  // MYN-202: one named colleague per sector, full width under the mockup's rows.
  colleagues: {
    title: C.COLLEAGUES,
    fields: ['colleagueContacts'],
    span: 12,
  },
};

/**
 * Card order on the page: About across the top (the mockup's identity card,
 * now that the Option B header holds the name), then What we invest in beside
 * Founder preferences, and Location beside Preferred geographies. Colleague
 * contacts (MYN-202, added after the mockup) take the full row underneath.
 */
export const INVESTOR_SECTION_ORDER: readonly InvestorSectionId[] = [
  'about',
  'preferences',
  'founder',
  'location',
  'geographies',
  'colleagues',
];

/** What the side panel edits: one section, or every section at once for a new profile (FR-181). */
export type InvestorEditorTarget = InvestorSectionId | 'setup';

/** The one-panel setup for a profile that does not exist yet: every section's fields. */
export const INVESTOR_SETUP: InvestorSectionConfig = {
  title: L.SETUP_TITLE,
  fields: INVESTOR_SECTION_ORDER.flatMap((id) => INVESTOR_SECTIONS[id].fields),
  span: 12,
};

export function investorEditorConfig(target: InvestorEditorTarget): InvestorSectionConfig {
  return target === 'setup' ? INVESTOR_SETUP : INVESTOR_SECTIONS[target];
}
